import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'

import { useDispatch, useSelector } from 'store/hooks'
import { endScanSesion, startScanSession } from 'store/scan-session/actions'
import type {
  IValidationScanSessionMessageCallbackParams,
  IValidationStartScanSessionCallbackParams,
} from 'store/scan-session/types'

import {
  createApiCall,
  projectResolution,
  scanMissMessage,
  scanResolveApi,
  statusOf,
  type ResolvedEntity,
  type ScanFilterEntity,
} from 'services'

// Direct file imports (not the `contexts` barrel) — this file is part of that barrel, so
// importing back through it is a circular import.
import { useEncodingDevice } from '../EncodingDeviceContext/EncodingDeviceContext'
import { useSnackbar } from '../SnackbarContext/useSnackbar'

/**
 * WHERE SCANS LAND — the routing layer for scan-to-filter.
 *
 * The model, corrected 2026-08-10. Scanning does NOT maintain a list of its own: it routes into
 * the narrowing controls that already exist. A scan session is opened once, one control is
 * nominated as the TARGET, and every tag scanned from then on is resolved to that control's
 * entity and applied there. There is no second inventory of "scanned things" to reconcile with
 * the filter, because the filter IS the list.
 *
 * The targets are the filter drawer's fields AND the list toolbar's search box, and they share
 * ONE `target` slot — aiming any of them un-aims the rest. That single-target invariant is the
 * whole point: the lit control is the only thing telling an operator where the next tap will
 * land, so two lit controls means a scan arrives somewhere they cannot predict. (See
 * `ScanRoutingTargetKind` for why the search box is not modelled as a pseudo-column.)
 *
 * WHY THIS LIVES ABOVE THE FIELDS, not in them. Three things outlive the surface that started
 * them, and every one of them breaks if the state sits in a component:
 *
 *  1. The SESSION outlives the modal. The modal exists only to pair a phone; once paired it has
 *     nothing left to show, so it closes. A session owned by that modal would die with it, and
 *     the operator would have to re-pair a phone for every field they aim at.
 *  2. The SESSION outlives ANY ONE target. MOVING the target — aim Product, walk a tray, aim
 *     Owner, keep going — is purely a routing change: no new session, no new SSE stream, no QR,
 *     no re-pair. What ends a session is arriving at NO target at all (below).
 *  3. The SELECTIONS outlive the field. The filter drawer unmounts its fields when it closes
 *     (MUI `Drawer` does not keep children mounted). If the accumulated scans lived in
 *     `SearchFilter`, closing the drawer would throw them away and every subsequent scan would
 *     have nowhere to land. They live here and the field reads them.
 *
 * SESSION LIFETIME (revised 2026-08-11, amended 2026-08-15).
 * A session ends when:
 *
 *  - the lit scan icon is switched OFF BY HAND, so the operator has said "I'm done". That is an
 *    explicit stop, and it is the only expression of one short of walking away;
 *  - the phone ends it from its own End Session control;
 *  - the server-side TTL expires (nominally 10 minutes — see the TD note on `TTL_10_MINUTES`
 *    in `scan-session.redis.repository.ts`).
 *
 * A session does NOT end when the target is merely RELEASED (`releaseTarget`) — closing the
 * filters drawer does that, and ending the session there would force a QR re-pair for the cost
 * of closing a panel, which is expensive enough that an operator would avoid closing it. So
 * "session open, nothing aimed" is a real resting state now, where it used to be only a race.
 * It is IDLE, and idle is announced rather than inferred: the release itself toasts, a tap that
 * arrives while idle toasts (see `land`), and no control is lit, so nothing on screen claims a
 * scan will land anywhere.
 *
 * ON BOTH SURFACES (VRG-289). All three of those signals are in this browser, and the operator
 * walking a rail is looking at the PHONE. The aim is reported to the server on every change
 * (`reportAim`) so the phone can carry the same fact — a standing banner while the session sits
 * idle, and a per-tap reply saying the tag was not used. Without it a tap into an idle session
 * came back to the phone as an ordinary `existing` + `filter` verdict and rendered as
 * "Certificate found": a success for a tag that was dropped, on the one surface the operator
 * could actually see.
 *
 * Every one of those paths must clear the REDUX session id as well as this provider's local
 * state. They are two halves of one fact, and letting them disagree wedges the feature: the id
 * is what `aim` reads to decide "a session already exists", so a stale id makes every later aim
 * a silent no-op — the operator lights a field and no scan ever arrives again. That is why the
 * remote-end path below dispatches the same teardown the toolbar does rather than only
 * resetting local state.
 */

/**
 * WHAT KIND of control is aimed. There is exactly ONE target at a time across the whole app, and
 * it is not always a filter column: the list toolbar's own search box is a target too.
 *
 * This exists so a non-column target can be represented honestly. The alternative — giving the
 * toolbar search a made-up `column.field` — would make it indistinguishable from a real filter
 * column, so nothing could tell "aimed at the Certificate column" from "aimed at the search box",
 * and a column that ever happened to share the invented name would silently collide with it.
 */
export type ScanRoutingTargetKind = 'filter-field' | 'search'

/** The routing key for the toolbar search. Never a column name — see `ScanRoutingTargetKind`. */
export const SEARCH_SCAN_TARGET_KEY = 'toolbar-search'

/**
 * WHICH PHYSICAL THING delivers the next tap.
 *
 * Canonical here rather than in the picker that renders it, because it is a property of the
 * SESSION, not of one control: `aim` and `chooseSource` both branch on it, the row component
 * renders it, and the storage helper persists it. One union, so a third member could not be added
 * to the picker without every consumer being made to answer for it.
 *
 * NOT a list of everything that can scan — a session accepts a phone AND a reader concurrently
 * (`ScanSessionBlock`'s two rows are both live at once, which is why the radio there became a
 * check). This says which one the operator asked to START with, which is the decision that was
 * previously being made for them by whatever happened to be plugged in.
 */
export type ScanSource = 'phone' | 'reader'

export interface ScanRoutingTarget {
  /** Which control this is. Compare THIS, not the spelling of `key`, to tell targets apart. */
  kind: ScanRoutingTargetKind
  /** Stable key for the target: a filter's `column.field`, or `SEARCH_SCAN_TARGET_KEY`. */
  key: string
  /** Which entity a tag resolves to here (Product → product, Owner → owner, …). */
  entity: ScanFilterEntity
  /** The target's visible name, so a message can say where a scan landed. */
  label: string
}

interface ScanRoutingValue {
  target: ScanRoutingTarget | null
  /** A session exists server-side. Says nothing about whether anything can scan INTO it. */
  sessionActive: boolean
  /**
   * A session exists AND something can actually deliver into it — a paired phone or a connected
   * desk reader. This is the difference between "aimed" and "ready", and it is the ONE signal the
   * scan controls light on.
   *
   * It exists because `sessionActive` alone is not enough to promise an operator that a tap will
   * arrive. The pairing modal can be dismissed without pairing, or the QR can go unanswered; both
   * leave a live session with no scanner attached, so every tap goes nowhere. A control lit on
   * `sessionActive` in that state tells the operator "ready" while nothing can arrive — the exact
   * lie this field removes.
   *
   * The same expression already decided whether `aim` needed to re-present the QR (below), so
   * readiness was being computed there and nowhere else. Naming it here gives both surfaces one
   * definition instead of two hand-rolled copies that can drift.
   */
  scannerReady: boolean
  /** A session has been asked for and has not answered yet. */
  starting: boolean
  phoneConnected: boolean
  readerConnected: boolean
  registerDeviceURL: string | null
  connectOpen: boolean
  /** Open the pairing modal by hand (the toolbar's scan control). */
  openConnect: () => void
  closeConnect: () => void
  /**
   * Nominate (or un-nominate, when it is already the target) where scans land.
   *
   * There is ONE target for the whole app, so aiming anything un-aims whatever was aimed before:
   * a filter field un-aims the toolbar search control, and the toolbar search control un-aims the
   * filter field. Never two lit at once — the lit control is the only thing telling an operator
   * where the next tap will land, and two of them means it lands somewhere unpredictable.
   *
   * MOVING between targets never touches the session. Un-nominating the one lit control by
   * CLICKING IT OFF ends the session (see the lifetime note in the header) — that click is the
   * operator saying "I'm done", and it is their only way to say it. Contrast `releaseTarget`,
   * which reaches the same aimed-at-nothing state as a side effect of closing a panel and
   * therefore must NOT end anything.
   *
   * `source` says WHICH SCANNER the operator asked to start with, and it is honoured verbatim.
   * Omitting it keeps the old implicit behaviour (a connected reader wins, otherwise the QR),
   * which now exists only as a fallback for a caller that has not asked — see `chooseSource`.
   */
  aim: (target: ScanRoutingTarget, source?: ScanSource) => void
  /**
   * Change the scan source WITHOUT touching the target.
   *
   * Separate from `aim` because the two decisions are genuinely independent, and routing a source
   * change through `aim` would be actively wrong: aiming the ALREADY-aimed target is the un-aim
   * gesture, so an operator changing source from the lit control would end their session instead
   * of switching scanners. This is the mid-session "the reader is at my desk and I am not" path.
   *
   * Adding a phone never removes the reader — the two are concurrent inputs into one session, so
   * this opens a way in rather than closing one off.
   */
  chooseSource: (source: ScanSource) => void
  /**
   * Stop routing scans anywhere, WITHOUT ending the session.
   *
   * WHY THIS IS NOT `endSession`, and why it will read like a regression in the diff: a filter
   * field's scan target may only be armed while the filters drawer is OPEN (VERA-455 D, revised
   * 2026-08-15), so closing the drawer releases it. Ending the session there would cost a QR
   * re-pair for the price of closing a panel, and an operator who learns that stops closing the
   * panel — which is exactly the invisible-armed-target state this change exists to remove.
   *
   * It deliberately does NOT fall back to the toolbar search target. Auto-retargeting would let
   * a NAVIGATION silently repoint where physical taps land, and with the drawer auto-applying,
   * the next tap would immediately narrow a list the operator never aimed at. Re-arming is an
   * explicit click, always.
   */
  releaseTarget: () => void
  endSession: () => void
  /** What has been scanned into a given field so far. */
  scannedFor: (key: string) => ResolvedEntity[]
  clearScanned: (key: string) => void
  /** Clear EVERY field's scans — what the filter drawer's Clear / Clear-all must mean. */
  clearAllScanned: () => void
  /** Keep only these ids — how an unchecked option stops being re-added by the next scan. */
  retainScanned: (key: string, ids: string[]) => void
  /** Called by a field while it is mounted, so a landing knows whether it is visible. */
  registerField: (key: string) => () => void
}

const EMPTY: ResolvedEntity[] = []

/**
 * COPY PARITY WITH THE PHONE. One outcome, one sentence, on both surfaces.
 *
 * The rule these three constants exist to enforce: where the SERVER supplies the sentence — an
 * ownership refusal, an unverifiable legacy tag, a not-shipped tag — BOTH surfaces render the
 * server's wording verbatim and neither paraphrases it. The phone already does (its relay shows
 * `err.message`, its stream shows `update.data.error`; see `SCAN_FEEDBACK` in
 * `apps/vera-app/app/scan-session/[sessionId].tsx`), and so does `handleMessage` below. The
 * constants here cover only the outcomes the server does NOT phrase, which are the two the
 * platform can hit on its own: it could not reach Vera, and the session is gone.
 */

/**
 * Transport failure. The request never reached Vera, so nothing is wrong with the tag — which
 * is the one thing the operator needs told, because their instinct is to re-tap harder.
 *
 * ONE sentence for BOTH places the platform can fail to reach the server: resolving a scanned
 * tag (`land`) and relaying a desk-reader scan. They previously had two different sentences
 * ("Could not look that tag up…" and "Hold the tag flat on the reader and try again"), the
 * second of which actively misdiagnoses a network failure as a bad tap.
 *
 * No em-dash, per working-conventions §15 — the phone's equivalent string carried one.
 */
const CANNOT_REACH_VERA = "Couldn't reach Vera. Check your connection and scan the tag again."

/**
 * The session is gone (expired, or ended from the phone).
 *
 * FIRST SENTENCE VERBATIM from the phone's `session-gone`. The second deliberately differs, and
 * that is not drift: the phone's tells the operator to start a new session "from the Vera
 * platform", advice that is meaningless when they are already looking at the platform. The
 * action differs by surface; the fact does not, so the fact is worded identically.
 */
const SESSION_GONE = 'This scan session has ended. Click a scan icon to start a new one.'

/**
 * The API's own pre-flight refusal when the session id is not live any more
 * (`admins.admin.controller.ts`, reader-scan / dev-scan: `Scan session not found or expired`).
 *
 * Matched on OUR OWN server string, and matched at all because it is the one reader-scan
 * failure that the SSE stream can never report — there is no session left to push down. Every
 * other refusal reaches `processCapturedScan`, which pushes it to the stream.
 */
const STALE_SESSION_RE = /scan session not found/i

/**
 * Where scans go, as it reads mid-sentence — "search", "the Owner filter".
 *
 * Branched on KIND, never on the spelling of the label: the toolbar search box is not a filter,
 * and templating one sentence off `target.label` is what once produced "the Search filter", a
 * control that does not exist (see the same note on `whereScansLand` in `ScanConnectModal`).
 */
const destinationPhrase = (target: ScanRoutingTarget): string =>
  target.kind === 'search' ? 'search' : `the ${target.label} filter`

/**
 * VRG-289 — TELL THE SERVER WHERE WE ARE AIMED, so the PHONE can say it too.
 *
 * The aim lives only in this provider, which was fine while the platform was the only surface
 * that had to know. It stopped being fine when releasing a target became a resting state
 * (`releaseTarget`): the operator walking a rail is looking at the phone, not at this browser,
 * and a tap into an idle session still came back to the phone as an ordinary `existing` +
 * `filter` verdict, which it rendered as "Certificate found". The honest signal was going to the
 * surface nobody was watching.
 *
 * FIRE AND FORGET, and that is a requirement rather than laziness. This browser is the surface
 * that actually DELIVERS the scan — it resolves the tag and checks it into the field — so a
 * failed POST must never take routing down with it. The worst case here is the phone showing a
 * stale aim for one poll interval; the worst case of awaiting it is a click that does nothing
 * because the network hiccuped.
 *
 * Only `filter` sessions may call this. Certificate sessions (minting, replace, held-in-transit)
 * have no target concept at all, so their context must keep no `aimed` key and the phone's idle
 * banner can never appear on one.
 *
 * THAT WAS ASSERTED HERE AND WAS FALSE. This provider is mounted app-wide and reported the aim
 * for whatever session id appeared in the redux slice — including the certificate session the
 * Create Certificates modal opens, which lands in the SAME slice. Measured on deployed dev:
 * `POST /toggle-scan-session` at 06:03:48.537 → `aimed: false` recorded against that session at
 * 06:03:48.931, 394ms later, with nobody having touched anything. The operator scanned a real
 * tag and the phone showed "No destination is selected" on a minting run.
 *
 * The caller is now gated on the session's PURPOSE (see the effect below), and the server refuses
 * an aim on a non-filter session as well (`updateScanSessionAim`) — the invariant belongs to the
 * session, not to the discipline of one browser.
 */
const reportAim = (sessionId: string | null, aimed: boolean, destination: string | null) => {
  if (!sessionId) return
  void createApiCall({
    method: 'POST',
    url: `/admins/admin/scan-session/${sessionId}/aim`,
    data: { aimed, destination },
  }).catch(() => {
    // Swallowed on purpose — see above. Not even a console.error: this fires on every target
    // change, and a session whose context TTL has lapsed answers `recorded: false` perfectly
    // normally.
  })
}

const ScanRoutingContext = createContext<ScanRoutingValue | null>(null)

const defaultValue: ScanRoutingValue = {
  target: null,
  sessionActive: false,
  scannerReady: false,
  starting: false,
  phoneConnected: false,
  readerConnected: false,
  registerDeviceURL: null,
  connectOpen: false,
  openConnect: () => undefined,
  closeConnect: () => undefined,
  aim: () => undefined,
  chooseSource: () => undefined,
  releaseTarget: () => undefined,
  endSession: () => undefined,
  scannedFor: () => EMPTY,
  clearScanned: () => undefined,
  clearAllScanned: () => undefined,
  retainScanned: () => undefined,
  registerField: () => () => undefined,
}

export const ScanRoutingProvider = ({ children }: { children: React.ReactNode }) => {
  const dispatch = useDispatch()
  const { open: notify } = useSnackbar()
  const { isConnected: readerConnected, subscribeScan } = useEncodingDevice()
  const scanSessionId = useSelector((state: any) => state.scanSession?.scanSessionId ?? null)
  // WHAT THE LIVE SESSION IS FOR. This provider sees every session in the app, not just the ones
  // it opened, so the id on its own does not say whether an aim is meaningful. See `reportAim`.
  const scanSessionPurpose = useSelector((state: any) => state.scanSession?.scanSessionPurpose ?? null)

  const [target, setTarget] = useState<ScanRoutingTarget | null>(null)
  const [starting, setStarting] = useState(false)
  const [phoneConnected, setPhoneConnected] = useState(false)
  const [registerDeviceURL, setRegisterDeviceURL] = useState<string | null>(null)
  const [connectOpen, setConnectOpen] = useState(false)
  const [scanned, setScanned] = useState<Record<string, ResolvedEntity[]>>({})

  /**
   * "Scans can actually arrive" — a session AND a scanner attached to it. See `scannerReady` on
   * the value type for why a session on its own does not qualify. Both the QR decision in `aim`
   * and the controls' lit state read this one expression.
   */
  const scannerReady = Boolean(scanSessionId) && (phoneConnected || readerConnected)

  // The SSE handler is registered ONCE, when the session starts, so anything it reads must be a
  // ref. A prop or state read directly inside it would be frozen at the value it had when the
  // session opened — which is before any field has been aimed at, i.e. always null.
  const targetRef = useRef<ScanRoutingTarget | null>(null)
  useEffect(() => {
    targetRef.current = target
  }, [target])
  // Same reason as `targetRef`: the SSE handler is registered once, so a landing that needs to
  // know whether a session is still open has to read it from a ref rather than close over it.
  const sessionActiveRef = useRef(false)
  useEffect(() => {
    sessionActiveRef.current = Boolean(scanSessionId)
  }, [scanSessionId])

  /**
   * VRG-289 — publish the aim to the server whenever it changes. See `reportAim`.
   *
   * ONE EFFECT, not a call at each of `aim` / `releaseTarget` / `endSession`. Those three are
   * not actually the only ways the pair (session, target) moves: a session ALSO arrives
   * asynchronously — `aim` dispatches `startScanSession` and the id lands a round trip later, so
   * the very first aim happens with no session to report against. Three manual call sites would
   * miss that one and leave the phone with no destination for the first field of every run.
   * Deriving it from the state instead means every path that can change either half is covered
   * by construction, including the ones added later.
   *
   * Nothing is reported once the session id is gone: `endSession` clears it, and an aim for a
   * dead session is a no-op the server answers `recorded: false` anyway.
   *
   * GATED ON PURPOSE, and that gate is the fix. Deriving from state is what made the effect
   * correct about every path; it is also what made it fire for sessions this provider never
   * opened. `filter` is required POSITIVELY — an unknown purpose reports nothing, because the
   * cost of a missed banner on a filter run is one wasted tap, and the cost of a spurious one on
   * a minting run is telling an operator their scan went nowhere while it is landing fine.
   */
  useEffect(() => {
    if (!scanSessionId) return
    if (scanSessionPurpose !== 'filter') return
    reportAim(scanSessionId, Boolean(target), target ? destinationPhrase(target) : null)
  }, [scanSessionId, scanSessionPurpose, target])
  const mountedFieldsRef = useRef<Set<string>>(new Set())
  const notifyRef = useRef(notify)
  useEffect(() => {
    notifyRef.current = notify
  }, [notify])
  // Tags already resolved, so a session re-reporting a tag (or a double tap) issues one lookup
  // and adds one row rather than N.
  const seenTagsRef = useRef<Set<string>>(new Set())

  const reset = useCallback(() => {
    setPhoneConnected(false)
    setRegisterDeviceURL(null)
    setStarting(false)
    setConnectOpen(false)
    setTarget(null)
    seenTagsRef.current.clear()
  }, [])

  /**
   * Resolve a scanned tag and check it in whatever field is currently aimed at.
   *
   * A refusal here must never be silent — that has been this feature's recurring failure. With
   * the modal closed there is no inline surface left to write to, so refusals speak as toasts
   * (`working-conventions` toast policy: errors are inline WHERE THERE IS AN INLINE SURFACE;
   * an ambient event with no home on screen is exactly the "invisible → toast" case).
   */
  const land = useCallback(async (tagId: string) => {
    const aimed = targetRef.current
    if (!aimed) {
      // TWO different states arrive here, and telling an operator the wrong one is worse than
      // saying nothing — so they are separated on whether a session is still open.
      //
      // IDLE (session open, nothing aimed). A real resting state since 2026-08-15: closing the
      // filters drawer releases a filter target and keeps the session (see `releaseTarget`).
      // The tap genuinely had nowhere to go, and the fix is to pick somewhere.
      //
      // SWITCHED OFF (no session). A RACE, and a real one: `endSession` clears the target
      // synchronously while the saga's teardown — closing the EventSource, POSTing
      // `turnOn:false` — is async, so a scan the phone or reader already relayed can arrive down
      // the still-open stream with the session already gone. Same window when the phone ends it
      // from its end. Naming that as "no destination selected" would send the operator looking
      // for a scan icon to click when what actually happened is that scanning stopped.
      //
      // A tag that vanishes without a word is this feature's oldest complaint, so both are
      // announced rather than swallowed.
      notifyRef.current?.({
        type: 'warning',
        title: `Scanned ${tagId}`,
        text: sessionActiveRef.current
          ? 'No destination is selected, so this tag was not used. Click a scan icon to choose where scans land.'
          : 'Scanning was switched off, so this tag was not used. Click a scan icon to start scanning again.',
      })
      return
    }
    if (seenTagsRef.current.has(tagId)) return
    seenTagsRef.current.add(tagId)

    try {
      const [resolution] = await scanResolveApi.resolveByTags([tagId])
      const projected = resolution ? projectResolution(resolution, aimed.entity) : null
      if (!projected) {
        // Allow a re-scan: nothing was added, so the tag is not "seen".
        seenTagsRef.current.delete(tagId)
        notifyRef.current?.({
          type: 'warning',
          title: `Scanned ${tagId}`,
          text: scanMissMessage(resolution, aimed.entity),
        })
        return
      }
      let added = false
      setScanned(prev => {
        const current = prev[aimed.key] ?? []
        if (current.some(row => row.id === projected.id)) return prev
        added = true
        return { ...prev, [aimed.key]: [...current, projected] }
      })
      // Only speak when the operator cannot SEE it happen. The field's own checkbox is the
      // report when the drawer is open; a toast on top of it would be the redundant-success
      // noise the toast sweep just removed. With the drawer closed nothing is visible, so the
      // scan would otherwise land in complete silence.
      if (!mountedFieldsRef.current.has(aimed.key)) {
        notifyRef.current?.({
          type: 'success',
          title:
            aimed.kind === 'search'
              ? `Searching for ${projected.label}`
              : added
                ? `${projected.label} added to the ${aimed.label} filter`
                : `${projected.label} is already in the ${aimed.label} filter`,
        })
      }
    } catch {
      seenTagsRef.current.delete(tagId)
      notifyRef.current?.({
        type: 'error',
        title: `Scanned ${tagId}`,
        text: CANNOT_REACH_VERA,
      })
    }
  }, [])

  const handleMessage = useCallback(
    (params: IValidationScanSessionMessageCallbackParams) => {
      if (params?.deviceRegistered) {
        setPhoneConnected(true)
        // The modal's ONLY job is to pair a device. Paired, it has nothing left to say, and
        // leaving it up would hide the very filter fields the scans are about to fill in.
        setConnectOpen(false)
        return
      }
      if (params?.sessionEnded) {
        // THE REDUX ID MUST GO TOO. `reset()` alone clears only this provider's local state and
        // leaves `state.scanSession.scanSessionId` pointing at a session that no longer exists.
        // `aim` reads that id to decide "a session already exists, just move the target", so a
        // stale one made every later aim a silent no-op: the operator lights a field, no
        // session is opened, no QR is offered, and no scan ever arrives again with nothing on
        // screen to say why. Dispatching the same teardown the toolbar uses closes the
        // EventSource and clears the id through the one existing path (the `turnOn:false` POST
        // it also sends is a harmless no-op against an already-dead session, and guards against
        // the opposite case — a session the phone reported as ended but the server still holds).
        dispatch(endScanSesion())
        reset()
        return
      }
      if (params?.status === 'removed') return
      if (params?.status === 'error') {
        // The server's own refusal text (ownership, decode failure), verbatim. It names the tag
        // and ends with the support sentence.
        notifyRef.current?.({
          type: 'error',
          title:
            params.tagId && params.tagId !== 'unknown'
              ? `Tag ${params.tagId} was not added`
              : 'That scan was refused',
          text: params.error || 'The scan was refused.',
        })
        return
      }
      if (!params?.tagId) return
      void land(params.tagId)
    },
    [dispatch, land, reset],
  )

  const handleStarted = useCallback((params: IValidationStartScanSessionCallbackParams) => {
    setStarting(false)
    if (params?.success) setRegisterDeviceURL(params.registerDeviceURL)
  }, [])

  const ensureSession = useCallback(() => {
    if (scanSessionId || starting) return
    setStarting(true)
    dispatch(startScanSession(handleStarted, handleMessage, undefined, 'filter'))
  }, [dispatch, handleMessage, handleStarted, scanSessionId, starting])

  const endSession = useCallback(() => {
    dispatch(endScanSesion())
    reset()
  }, [dispatch, reset])

  const openConnect = useCallback(() => {
    ensureSession()
    setConnectOpen(true)
  }, [ensureSession])

  /**
   * Release the target, keep the session. See the prop's doc on `ScanRoutingValue` for why this
   * is not `endSession` and why it must not auto-retarget.
   *
   * IT SPEAKS. Everything else that changes where scans land is something the operator did TO
   * the scan control, with the control right there changing under the click. This one is a
   * side effect of closing a panel, and the control that would have shown it unmounts with that
   * panel, so the state change has no on-screen witness at all. That is the "invisible → toast"
   * case exactly, and the state it announces (a live session aimed at nothing) is the one that
   * manufactures "I scanned and nothing happened".
   */
  const releaseTarget = useCallback(() => {
    const current = targetRef.current
    if (!current) return
    // The SERVER is told too, by the effect that watches `target` — so the phone in the
    // operator's hand shows the same idle state this toast announces, rather than reporting
    // "Certificate found" for a tag that went nowhere. See `reportAim`.
    setTarget(null)
    notifyRef.current?.({
      type: 'warning',
      title: `Scans are no longer going to ${destinationPhrase(current)}`,
      text: 'Scanning is still on. Click a scan icon to choose where the next tag lands.',
    })
  }, [])

  /**
   * Commit to a scan source: open a session if there isn't one, and show the QR only if the
   * operator asked for the phone.
   *
   * THE WHOLE POINT IS THAT THIS IS TOLD, NOT INFERRED. The branch below used to read
   * `if (!readerConnected) setConnectOpen(true)` — the source decided by what happened to be
   * plugged in — which is what made the reader claim every session silently and made the QR the
   * consolation prize for not owning hardware.
   *
   * `ensureSession` no-ops while a session exists or is starting, and `registerDeviceURL` is
   * untouched, so choosing the phone mid-session re-presents the SAME code for the SAME session
   * rather than restarting anything.
   */
  const chooseSource = useCallback(
    (source: ScanSource) => {
      ensureSession()
      // A desk reader needs no pairing step at all: its scans are relayed into the session by the
      // subscriber below the moment a session id exists. Showing a QR for it would be a step that
      // does nothing.
      if (source === 'phone') setConnectOpen(true)
    },
    [ensureSession],
  )

  const aim = useCallback(
    (next: ScanRoutingTarget, source?: ScanSource) => {
      // Read the committed target ONCE and drive both the state change and the session decision
      // from it. These used to be computed separately — `setTarget`'s updater compared against
      // `prev` while the session branch compared against `targetRef` — which left two sources of
      // truth for one question ("is this an un-aim?") that were only ever equal by luck.
      // Kind AND key, because a filter column and the search box are different targets even if
      // their keys ever collide.
      const current = targetRef.current
      const unaiming = current?.kind === next.kind && current?.key === next.key

      if (unaiming) {
        // NOTHING IS AIMED ANY MORE, so the session ends (revised 2026-08-11 — see the header's
        // lifetime note). There is exactly one target, so un-aiming it IS "all scan buttons are
        // clicked off"; no separate count is needed. `endSession` clears the target as part of
        // its reset, so this branch must not also set it.
        endSession()
        return
      }

      setTarget(next)

      // THE OPERATOR SAID WHICH SCANNER. Honour it verbatim and stop — including when a scanner
      // is already attached, because "I want the phone" is a real instruction even with a reader
      // plugged in, and the early return below would silently swallow it.
      if (source) {
        chooseSource(source)
        return
      }

      // MOVING THE TARGET RESTARTS NOTHING.
      //
      // With a session open and a scanner actually attached to it, aiming another field is
      // purely a routing change: no `toggle-scan-session`, no second SSE stream, no QR, and
      // above all no invitation to re-pair a phone that is already paired.
      if (scannerReady) return

      // NO SOURCE WAS STATED — the legacy implicit path, kept only as a fallback.
      //
      // It is the behaviour this feature exists to remove: a connected reader claims the session
      // silently and everyone else gets the QR, with the operator never asked. `ScanTargetButton`
      // no longer reaches here (it either passes a source or opens the picker), so this covers a
      // caller that has not been taught to choose. Left in rather than made to throw, because the
      // failure mode of the old behaviour is a working scan session, and the failure mode of
      // throwing is a dead button.
      ensureSession()
      if (!readerConnected) setConnectOpen(true)
    },
    [chooseSource, endSession, ensureSession, readerConnected, scannerReady],
  )

  // Desk-reader ingest. The helper hands over the tag's encrypted `e=` token, which only the
  // server can decode, so it is relayed into the same session a phone would post to and comes
  // back through the same SSE path. Package handles are not tags — they belong to the
  // create-certificates flow and would surface a false "scan failed" here.
  useEffect(() => {
    if (!scanSessionId) return
    const unsub = subscribeScan(async e => {
      if (/\/pkg\/[0-9a-f-]{36}/i.test(e)) return
      try {
        await createApiCall({
          method: 'POST',
          url: `/admins/admin/scan-session/${scanSessionId}/reader-scan`,
          data: { e },
        })
      } catch (error) {
        // Two different failures arrive here and they get different treatment, so they are
        // told apart by whether the server ANSWERED: a refusal carries a status, a transport
        // failure does not. This used to be an `if` on a resolved response's status, which
        // stopped running when `createApiCall` began rejecting (VRG-386) — taking the stale-
        // session teardown below with it, so a dead session no longer self-healed and every
        // refusal fell through to the transport sentence instead.
        const status = statusOf(error)

        if (status !== undefined) {
          const detail = String((error as any)?.response?.data?.message ?? '')
          console.error('[reader-scan] refused:', detail)

          if (STALE_SESSION_RE.test(detail)) {
            // The session is gone and the stream cannot say so — there is nothing left to push
            // down it. Tear the local session down rather than only complaining: this is the
            // same wedge as a missed `sessionEnded` (a live-looking id with no session behind
            // it), and clearing it is what lets the next aim open a working one.
            dispatch(endScanSesion())
            reset()
            notifyRef.current?.({
              type: 'error',
              title: 'That scan was not added',
              text: SESSION_GONE,
            })
            return
          }

          // ONE OUTCOME, ONE SENTENCE — so this branch says nothing.
          //
          // Every other refusal got as far as `processCapturedScan`, which pushes the SAME
          // refusal down the SSE stream before rethrowing (see its catch in
          // `scan-session.service.ts`). `handleMessage` renders that with the server's own
          // wording, verbatim and identically to the phone. Speaking here too put TWO
          // different sentences on screen for ONE tap, and the one this branch used to
          // contribute — "Hold the tag flat on the reader and try again" — is wrong advice for
          // the refusals it most often fired on: an ownership refusal or an unverifiable
          // legacy tag has nothing to do with how the tag was held. The server's message is
          // deliberately NOT echoed here either; it can carry raw internals for a generic
          // failure, which is why `ScanSessionBlock` logs it rather than rendering it.
          return
        }

        // No status, so the POST never completed and no SSE event exists for this scan — no
        // other voice will report it. This is the one reader failure this branch may speak
        // for, and it is a transport failure, not a tag failure — hence the shared sentence.
        notifyRef.current?.({
          type: 'error',
          title: 'That scan was not sent',
          text: CANNOT_REACH_VERA,
        })
      }
    })
    return unsub
  }, [dispatch, reset, scanSessionId, subscribeScan])

  const registerField = useCallback((key: string) => {
    mountedFieldsRef.current.add(key)
    return () => {
      mountedFieldsRef.current.delete(key)
    }
  }, [])

  const scannedFor = useCallback((key: string) => scanned[key] ?? EMPTY, [scanned])

  const clearScanned = useCallback((key: string) => {
    setScanned(prev => {
      if (!prev[key]?.length) return prev
      const next = { ...prev }
      delete next[key]
      return next
    })
    // The tags may be re-scanned into a cleared field.
    seenTagsRef.current.clear()
  }, [])

  /**
   * Clear every field at once.
   *
   * The drawer's Clear / Clear-all wipes the staged filters, and without this the scanned set
   * would immediately re-stage itself — the field would show its Clear affordance again and the
   * filter would come back, so Clear would appear not to work. Whatever clears a filter has to
   * clear the scans that feed it.
   */
  const clearAllScanned = useCallback(() => {
    setScanned(prev => (Object.keys(prev).length ? {} : prev))
    seenTagsRef.current.clear()
  }, [])

  const retainScanned = useCallback((key: string, ids: string[]) => {
    const keep = new Set(ids)
    setScanned(prev => {
      const current = prev[key]
      if (!current?.length) return prev
      const next = current.filter(row => keep.has(row.id))
      if (next.length === current.length) return prev
      current
        .filter(row => !keep.has(row.id))
        .forEach(row => seenTagsRef.current.delete(row.tagId))
      return { ...prev, [key]: next }
    })
  }, [])

  const value = useMemo<ScanRoutingValue>(
    () => ({
      target,
      sessionActive: Boolean(scanSessionId),
      scannerReady,
      starting,
      phoneConnected,
      readerConnected,
      registerDeviceURL,
      connectOpen,
      openConnect,
      closeConnect: () => setConnectOpen(false),
      aim,
      chooseSource,
      releaseTarget,
      endSession,
      scannedFor,
      clearScanned,
      clearAllScanned,
      retainScanned,
      registerField,
    }),
    [
      target,
      scanSessionId,
      scannerReady,
      starting,
      phoneConnected,
      readerConnected,
      registerDeviceURL,
      connectOpen,
      openConnect,
      aim,
      chooseSource,
      releaseTarget,
      endSession,
      scannedFor,
      clearScanned,
      clearAllScanned,
      retainScanned,
      registerField,
    ],
  )

  return <ScanRoutingContext.Provider value={value}>{children}</ScanRoutingContext.Provider>
}

export function useScanRouting(): ScanRoutingValue {
  return useContext(ScanRoutingContext) ?? defaultValue
}
